import useBfcmCalendarQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBfcmCalendarQuery';
import { useBfcmCarouselsQuery } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBfcmCarouselsQuery';
import useBlogCategoryQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBlogCategoryQuery';
import { useG2BadgesLogosQuery } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useG2BadgesLogosQuery';
import { useGlossaryPagesQuery } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useGlossaryPagesQuery';
import useLatestBlogPostsQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useLatestBlogPostsQuery';
import useNewsfeedQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useNewsfeedQuery';
import useResourcesQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useResourcesQuery';
import useTaxonomyQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useTaxonomyQuery';
import useWhatsNewUpdateQuery from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useWhatsNewUpdates';


import { EagerLoadingContextProvider } from '@klaviyo-mkt/gatsby-components/context/eager-loading';

import type { IAccordion } from '@klaviyo-mkt/gatsby-components/organisms/accordion/types';
import type { IAdvancedIframe } from '@klaviyo-mkt/gatsby-components/organisms/advanced-iframe/types';
import type { IAnchorLinks } from '@klaviyo-mkt/gatsby-components/organisms/anchor-links/types';
import type { IAudience } from '@klaviyo-mkt/gatsby-components/organisms/audience/types';
import type { IBfcmCalendar } from '@klaviyo-mkt/gatsby-components/organisms/bfcm-calendar/types';
import type { IBfcmFeatureCard } from '@klaviyo-mkt/gatsby-components/organisms/bfcm-feature-card/types';
import type { IBfcmHero } from '@klaviyo-mkt/gatsby-components/organisms/bfcm-hero/types';
import type { IBlogCards } from '@klaviyo-mkt/gatsby-components/organisms/blog-cards/types';
import type { ICtaStatement } from '@klaviyo-mkt/gatsby-components/organisms/cta-statement/types';
import type { IBulletedList } from '@klaviyo-mkt/gatsby-components/organisms/bulleted-list';
import type { IBulletedLists } from '@klaviyo-mkt/gatsby-components/organisms/bulleted-lists/types';
import type { IChart } from '@klaviyo-mkt/gatsby-components/organisms/chart/types';
import type { IComparisonChart } from '@klaviyo-mkt/gatsby-components/organisms/comparison-chart/types';
import type { IContentCarouselProps } from '@klaviyo-mkt/gatsby-components/organisms/content-carousel/types';
import type { ICountdownHero } from '@klaviyo-mkt/gatsby-components/organisms/countdown-hero/types';
import type { ICsat } from '@klaviyo-mkt/gatsby-components/organisms/csat/types';
import type { IEnhancedCarousel } from '@klaviyo-mkt/gatsby-components/organisms/enhanced-carousel/types';
import type { IEmailSubjectLineGenerator } from '@klaviyo-mkt/gatsby-components/organisms/email-subject-line-generator/types';
import type { IFeatures } from '@klaviyo-mkt/gatsby-components/organisms/features/types';
import type { IFeaturesListProps } from '@klaviyo-mkt/gatsby-components/organisms/features-list/types';
import type { IFooterCta } from '@klaviyo-mkt/gatsby-components/organisms/footer-cta/types';
import type { IFormsCta } from '@klaviyo-mkt/gatsby-components/organisms/forms-cta/types';
import type { IFullColumn } from '@klaviyo-mkt/gatsby-components/organisms/full-column/types';
import type { IFullColumnImageCta } from '@klaviyo-mkt/gatsby-components/organisms/full-column-image-cta/types';
import type { IFullColumnTextCta } from '@klaviyo-mkt/gatsby-components/organisms/full-column-text-cta/types';
import type { IGallery } from '@klaviyo-mkt/gatsby-components/organisms/gallery/types';
import type { IGlossary } from '@klaviyo-mkt/gatsby-components/organisms/glossary/types';
import type { IHomepageHero } from '@klaviyo-mkt/gatsby-components/organisms/homepage-hero/types';
import type { IIframeProps } from '@klaviyo-mkt/gatsby-components/atoms/iframe/types';
import type { IJobSearch } from '@klaviyo-mkt/gatsby-components/organisms/job-search/types';
import type { IJumpAnchor } from '@klaviyo-mkt/gatsby-components/atoms/jump-anchor/types';
import type { IKlaviyoFormOrganism } from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-form/types';
import type { ILogosProps } from '@klaviyo-mkt/gatsby-components/organisms/logos/types';
import type { IMonoHomepage } from '@klaviyo-mkt/gatsby-components/organisms/mono-homepage/types';
import type { INavigationCategorySelection } from '@klaviyo-mkt/gatsby-components/organisms/navigation-category-selection/types';
import type { IOffsetImageHero } from '@klaviyo-mkt/gatsby-components/organisms/offset-image-hero/types';
import type { IPostFilter, ITermNode } from '@klaviyo-mkt/gatsby-components/organisms/post-filter/types';
import type { IPricing } from '@klaviyo-mkt/gatsby-components/organisms/pricing/types';
import type { IPricingV2 } from '@klaviyo-mkt/gatsby-components/organisms/pricing-v2/types';
import type { IProducts } from '@klaviyo-mkt/gatsby-components/organisms/products/types';
import type { IRip } from '@klaviyo-mkt/gatsby-components/organisms/rip/types';
import type { IResourceCards } from '@klaviyo-mkt/gatsby-components/organisms/resource-cards/types';
import type { IRive } from '@klaviyo-mkt/gatsby-components/organisms/rive/types';
import type { IRoiCalculator } from '@klaviyo-mkt/gatsby-components/organisms/roi-calculator/types';
import type { IScrollAccordion } from '@klaviyo-mkt/gatsby-components/organisms/scroll-accordion/types';
import type { IScrollSocialProof } from '@klaviyo-mkt/gatsby-components/organisms/scroll-social-proof/types';
import type { ISearchBar } from '@klaviyo-mkt/gatsby-components/organisms/search-bar/types';
import type { ISearchHero } from '@klaviyo-mkt/gatsby-components/organisms/search-hero/types';
import type { ISectionHeader } from '@klaviyo-mkt/gatsby-components/organisms/section-header/types';
import type { ISocial } from '@klaviyo-mkt/gatsby-components/organisms/social/types';
import type { IStats } from '@klaviyo-mkt/gatsby-components/organisms/stats/types';
import type { ISocialProof } from '@klaviyo-mkt/gatsby-components/organisms/social-proof';
import type { ITabbedContent } from '@klaviyo-mkt/gatsby-components/organisms/tabbed-content/types';
import type { ISolutionsScrollAccordion } from '@klaviyo-mkt/gatsby-components/organisms/solutions-scroll-accordion/types';
import type { ITestimonialsTwoColumnCMS } from '@klaviyo-mkt/gatsby-components/organisms/testimonials-two-column/types';
import type { ITextTagline } from '@klaviyo-mkt/gatsby-components/organisms/text-tagline/types';
import type { IVideoHero } from '@klaviyo-mkt/gatsby-components/organisms/video-hero/types';
import type { IVideoMediaProps } from '@klaviyo-mkt/gatsby-components/organisms/video/types';
import type { IWhatsNewTimeline } from '@klaviyo-mkt/gatsby-components/organisms/whats-new-timeline/types';
// ORGANISM_TYPE_IMPORTS

import type { TColorVariant } from '@klaviyo-mkt/gatsby-components/shared/styles/types';

export interface IComponentLayout {
  accordion: IAccordion;
  advancedIframe: IAdvancedIframe;
  anchorLinks: IAnchorLinks;
  audience: IAudience;
  bfcmCalendar: IBfcmCalendar;
  bfcmFeatureCard: IBfcmFeatureCard;
  bfcmHero: IBfcmHero;
  blogCards: IBlogCards;
  backgroundColor: TColorVariant;
  ctaStatement: ICtaStatement;
  bulletedList: IBulletedList;
  bulletedLists: IBulletedLists;
  chart: IChart;
  comparisonChart: IComparisonChart;
  contentCarousel: IContentCarouselProps;
  countdownHero: ICountdownHero;
  csat: ICsat;
  enhancedCarousel: IEnhancedCarousel;
  emailSubjectLineGenerator: IEmailSubjectLineGenerator;
  features: IFeatures;
  featuresList: IFeaturesListProps;
  fieldGroupName: string;
  footerCta: IFooterCta;
  formsCta: IFormsCta;
  fullColumn: IFullColumn;
  fullColumnImageCta: IFullColumnImageCta;
  fullColumnTextCta: IFullColumnTextCta;
  g2Badges: ILogosProps;
  gallery: IGallery;
  glossary: IGlossary;
  homepageHero: IHomepageHero;
  iframe: IIframeProps;
  jobSearch: IJobSearch;
  jumpAnchor: IJumpAnchor;
  klaviyoForm: IKlaviyoFormOrganism;
  logosOrganism: ILogosProps;
  monoHomepage: IMonoHomepage;
  navigationCategorySelection: INavigationCategorySelection;
  offsetImageHero: IOffsetImageHero;
  pricing: IPricing;
  pricingV2: IPricingV2;
  products: IProducts;
  resourceCards: IResourceCards;
  rip: IRip;
  rive: IRive;
  roiCalculator: IRoiCalculator;
  scrollAccordion: IScrollAccordion;
  scrollSocialProof: IScrollSocialProof;
  searchBar: ISearchBar;
  searchHero: ISearchHero;
  sectionHeader: ISectionHeader;
  social: ISocial;
  socialProof: ISocialProof;
  stats: IStats;
  solutionsScrollAccordion: ISolutionsScrollAccordion;
  tabbedContent: ITabbedContent;
  testimonialsTwoColumn: ITestimonialsTwoColumnCMS;
  textTagline: ITextTagline;
  video: IVideoMediaProps;
  videoHero: IVideoHero;
  whatsNewTimeline: IWhatsNewTimeline;
  postFilter: IPostFilter;
  // ORGANISM_INTERFACES
  // TODO: Remove this once all the relevant components have been converted to TS
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
}


import Accordion from '@klaviyo-mkt/gatsby-components/organisms/accordion';
import { AdvancedIframe } from '@klaviyo-mkt/gatsby-components/organisms/advanced-iframe';
import { AnchorLinks } from '@klaviyo-mkt/gatsby-components/organisms/anchor-links';
import { Audience } from '@klaviyo-mkt/gatsby-components/organisms/audience';
import { BfcmCalendar } from '@klaviyo-mkt/gatsby-components/organisms/bfcm-calendar';
import BfcmFeatureCard from '@klaviyo-mkt/gatsby-components/organisms/bfcm-feature-card';
import { BfcmHero } from '@klaviyo-mkt/gatsby-components/organisms/bfcm-hero';
import BlogHero from '@klaviyo-mkt/gatsby-components/organisms/blog-hero';
import BlogCards from '@klaviyo-mkt/gatsby-components/organisms/blog-cards';
import BlogLogoTagline from '@klaviyo-mkt/gatsby-components/organisms/blog-logo-tagline';
import CtaStatement from '@klaviyo-mkt/gatsby-components/organisms/cta-statement';
import BulletedList from '@klaviyo-mkt/gatsby-components/organisms/bulleted-list';
import BulletedLists from '@klaviyo-mkt/gatsby-components/organisms/bulleted-lists';
import Cards from '@klaviyo-mkt/gatsby-components/organisms/cards';
import Chart from '@klaviyo-mkt/gatsby-components/organisms/chart';
import ComparisonChart from '@klaviyo-mkt/gatsby-components/organisms/comparison-chart';
import ContentCarousel from '@klaviyo-mkt/gatsby-components/organisms/content-carousel';
import Countdown from '@klaviyo-mkt/gatsby-components/organisms/countdown';
import CountdownHero from '@klaviyo-mkt/gatsby-components/organisms/countdown-hero';
import Csat from '@klaviyo-mkt/gatsby-components/organisms/csat';
import { EnhancedCarousel } from '@klaviyo-mkt/gatsby-components/organisms/enhanced-carousel';
import { EmailSubjectLineGenerator } from '@klaviyo-mkt/gatsby-components/organisms/email-subject-line-generator';
import { Features } from '@klaviyo-mkt/gatsby-components/organisms/features';
import FeaturesList from '@klaviyo-mkt/gatsby-components/organisms/features-list';
import { FlowsAi } from '@klaviyo-mkt/gatsby-components/organisms/flows-ai';
import FooterCta from '@klaviyo-mkt/gatsby-components/organisms/footer-cta';
import FormsCta from '@klaviyo-mkt/gatsby-components/organisms/forms-cta';
import FullColumn from '@klaviyo-mkt/gatsby-components/organisms/full-column';
import { FullColumnImageCta } from '@klaviyo-mkt/gatsby-components/organisms/full-column-image-cta';
import { FullColumnTextCta } from '@klaviyo-mkt/gatsby-components/organisms/full-column-text-cta';
import Gallery from '@klaviyo-mkt/gatsby-components/organisms/gallery';
import { Glossary } from '@klaviyo-mkt/gatsby-components/organisms/glossary';
import HomepageHero from '@klaviyo-mkt/gatsby-components/organisms/homepage-hero';
import Iframe from '@klaviyo-mkt/gatsby-components/atoms/iframe';
import JobSearch from '@klaviyo-mkt/gatsby-components/organisms/job-search';
import JumpAnchor from '@klaviyo-mkt/gatsby-components/atoms/jump-anchor';
import KlaviyoFormOrganism from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-form';
import Logos from '@klaviyo-mkt/gatsby-components/organisms/logos';
import MainCta from '@klaviyo-mkt/gatsby-components/organisms/main-cta';
import MonoHomepage from '@klaviyo-mkt/gatsby-components/organisms/mono-homepage';
import NavigationCategorySelection from '@klaviyo-mkt/gatsby-components/organisms/navigation-category-selection';
import { OffsetImageHero } from '@klaviyo-mkt/gatsby-components/organisms/offset-image-hero';
import { PostFilter } from '@klaviyo-mkt/gatsby-components/organisms/post-filter';
import Pricing from '@klaviyo-mkt/gatsby-components/organisms/pricing';
import { PricingV2 } from '@klaviyo-mkt/gatsby-components/organisms/pricing-v2';
import { Products } from '@klaviyo-mkt/gatsby-components/organisms/products';
import RelatedContent from '@klaviyo-mkt/gatsby-components/organisms/related-content';
import { ResourceCards } from '@klaviyo-mkt/gatsby-components/organisms/resource-cards';
import { Rip } from '@klaviyo-mkt/gatsby-components/organisms/rip';
import { Rive } from '@klaviyo-mkt/gatsby-components/organisms/rive';
import RoiCalculator from '@klaviyo-mkt/gatsby-components/organisms/roi-calculator';
import { ScrollAccordion } from '@klaviyo-mkt/gatsby-components/organisms/scroll-accordion';
import { ScrollSocialProof } from '@klaviyo-mkt/gatsby-components/organisms/scroll-social-proof';
import { SearchBar } from '@klaviyo-mkt/gatsby-components/organisms/search-bar';
import SearchHero from '@klaviyo-mkt/gatsby-components/organisms/search-hero';
import { SectionHeader } from '@klaviyo-mkt/gatsby-components/organisms/section-header';
import Social from '@klaviyo-mkt/gatsby-components/organisms/social';
import SocialProof from '@klaviyo-mkt/gatsby-components/organisms/social-proof';
import { SolutionsScrollAccordion } from '@klaviyo-mkt/gatsby-components/organisms/solutions-scroll-accordion';
import Stats from '@klaviyo-mkt/gatsby-components/organisms/stats';
import TabbedContent from '@klaviyo-mkt/gatsby-components/organisms/tabbed-content';
import TextTagline from '@klaviyo-mkt/gatsby-components/organisms/text-tagline';
import TestimonialsQuote from '@klaviyo-mkt/gatsby-components/organisms/testimonials-quote';
import TestimonialsTwoColumn from '@klaviyo-mkt/gatsby-components/organisms/testimonials-two-column';
import TileList from '@klaviyo-mkt/gatsby-components/organisms/tile-list';
import TwoColumn from '@klaviyo-mkt/gatsby-components/organisms/two-column';
import TwoColumnSlider from '@klaviyo-mkt/gatsby-components/organisms/two-column-slider';
import Video from '@klaviyo-mkt/gatsby-components/organisms/video';
import { VideoHero } from '@klaviyo-mkt/gatsby-components/organisms/video-hero';
import WhatsNewTimeline from '@klaviyo-mkt/gatsby-components/organisms/whats-new-timeline';
// ORGANISM_IMPORTS

const getComponent = (row: IComponentLayout) => {
  switch (row?.componentType) {
    case 'AccordionComponent':
      return <Accordion {...row.accordion} />;
    case 'AdvancedIframeComponent':
      return <AdvancedIframe { ...row.advancedIframe } key={JSON.stringify(row)} />;
    case 'AnchorLinksComponent':
      return <AnchorLinks { ...row.anchorLinks } key={JSON.stringify(row)} />;
    case 'AudienceComponent':
      return <Audience { ...row.audience } key={JSON.stringify(row)} />;
    case 'BfcmCalendarComponent':
      return <BfcmCalendar { ...row.bfcmCalendar } key={JSON.stringify(row)} />;
    case 'BfcmFeatureCardComponent':
      return <BfcmFeatureCard { ...row.bfcmFeatureCard } key={JSON.stringify(row)} />;
    case 'BfcmHeroComponent':
      return <BfcmHero { ...row?.bfcmHero } key={JSON.stringify(row)} />;
    case 'BlogHeroComponent':
      return <BlogHero {...row.blogHero} />;
    case 'BlogCardsComponent':
      return <BlogCards {...row.blogCards} />;
    case 'BlogLogotaglineComponent':
      return <BlogLogoTagline customPadding={row.customPadding} backgroundColor={row.backgroundColor} fadeIn={row.fadeIn}/>;
    case 'BrandStatementComponent':
      return <CtaStatement {...row.brandStatement} key={JSON.stringify(row)} />;
    case 'BulletedListComponent':
      return <BulletedList {...row.bulletedList} />;
    case 'BulletedListsComponent':
      return <BulletedLists {...row.bulletedLists} />;
    case 'CardsComponent':
      return <Cards {...row.cards} />;
    case 'ChartComponent':
      return <Chart {...row.charts} />;
    case 'ComparisonChartComponent':
      return <ComparisonChart {...row.comparisonChart} chart={row.comparisonChart?.chart === null ? undefined : row.comparisonChart?.chart} />;
    case 'ContentCarouselComponent':
      return <ContentCarousel {...row.contentCarousel} key={row?.id} />;
    case 'CountdownComponent':
      return <Countdown {...row.countdown} />;
    case 'CountdownHeroComponent':
      return <CountdownHero { ...row.countdownHero } key={JSON.stringify(row)} />;
    case 'CsatComponent':
      return <Csat {...row.csat} key={JSON.stringify(row)} />;
    case 'EmailSubjectLineGeneratorComponent':
      return <EmailSubjectLineGenerator { ...row.emailSubjectLineGenerator } key={JSON.stringify(row)} />;
    case 'EnhancedCarouselComponent':
      return <EnhancedCarousel { ...row.enhancedCarousel } key={JSON.stringify(row)} />;
    case 'FeaturesComponent':
      return <Features { ...row.features } key={JSON.stringify(row)} />;
    case 'FeaturesListComponent':
      return <FeaturesList {...row.featuresList} />;
    case 'FlowsAiComponent':
      return <FlowsAi { ...row.flowsAi } key={JSON.stringify(row)} />;
    case 'FooterCtaComponent':
      return <FooterCta {...row.footerCta} />;
    case 'FormsCtaComponent':
      return <FormsCta {...row.formsCta} />;
    case 'FullColumnComponent':
      return <FullColumn {...row.fullColumn} key={JSON.stringify(row.fullColumn)} />;
    case 'FullColumnImageCtaComponent':
      return <FullColumnImageCta { ...row.fullColumnImageCta } key={JSON.stringify(row)} />;
    case 'FullColumnTextCtaComponent':
      return <FullColumnTextCta { ...row.fullColumnTextCta } key={JSON.stringify(row)} />;
    case 'G2BadgesComponent': 
      return <Logos {...row.logosOrganism} />;
    case 'GalleryComponent':
      return <Gallery {...row.gallery} key={JSON.stringify(row.gallery)} />;
    case 'GlossaryComponent':
      return <Glossary { ...row } terms={row?.terms} sortedTerms={row?.sortedTerms} key={JSON.stringify(row)} />;
    case 'HomepageHeroComponent':
      return <HomepageHero { ...row.homepageHero } key={JSON.stringify(row)} />;
    case 'IframeComponent':
      return <Iframe {...row.iframe} key={JSON.stringify(row.iframe)} />;
    case 'JobSearchComponent':
      return <JobSearch {...row.jobSearch} />;
    case 'JumpAnchorComponent':
      return <JumpAnchor {...row.jumpAnchor} />;
    case 'KlaviyoFormComponent':
      return <KlaviyoFormOrganism {...row.klaviyoForm} />;
    case 'LogosComponent':
      return <Logos {...row.logosOrganism} />;
    case 'MainCtaComponent':
      return <MainCta {...row.mainCta} />;
    case 'MonoHomepageComponent':
      return <MonoHomepage {...row.monoHomepage} />;
    case 'NavigationCategorySelectionComponent':
      return <NavigationCategorySelection {...row} customPadding={row.customPadding} />;
    case 'OffsetImageHeroComponent':
      return <OffsetImageHero {...row.offsetImageHero} key={JSON.stringify(row)} />;
    case 'PostFilterComponent':
      return <PostFilter { ...row.postFilter } key={JSON.stringify(row)} />;
    case 'PricingComponent':
      return <Pricing {...row.pricing} />;
    case 'PricingV2Component':
      return <PricingV2 { ...row.pricing as IPricingV2 } key={JSON.stringify(row)} />;
    case 'ProductsComponent':
      return <Products { ...row.products } key={JSON.stringify(row)} />;
    case 'RelatedContentComponent':
      return <RelatedContent {...row.relatedContent} />;
    case 'ResourceCardsComponent':
      return <ResourceCards { ...row.resourceCards } key={JSON.stringify(row)} />;
    case 'RipComponent':
      return <Rip { ...row.rip } key={JSON.stringify(row)} />;
    case 'RiveComponent':
      return <Rive { ...row.rive } key={JSON.stringify(row)} />;
    case 'RoiCalculatorComponent':
      return <RoiCalculator {...row.roiCalculator} />;
    case 'ScrollAccordionComponent':
      return <ScrollAccordion { ...row.scrollAccordion } key={JSON.stringify(row)} />;
    case 'ScrollSocialProofComponent':
      return <ScrollSocialProof { ...row.scrollSocialProof } key={JSON.stringify(row)} />;
    case 'SearchBarComponent':
      return <SearchBar { ...row.searchBar } key={JSON.stringify(row)} />;
    case 'SearchHeroComponent':
      return <SearchHero { ...row.searchHero } key={JSON.stringify(row)} />;
    case 'SectionHeaderComponent':
      return <SectionHeader { ...row.sectionHeader } key={JSON.stringify(row)} />;
    case 'SocialComponent':
      return <Social { ...row.social } key={JSON.stringify(row)} />;
    case 'SocialProofComponent':
      return <SocialProof {...row.socialProof} />;
    case 'SolutionsScrollAccordionComponent':
      return <SolutionsScrollAccordion { ...row.solutionsScrollAccordion } key={JSON.stringify(row)} />;
    case 'StatsComponent':
      return <Stats {...row.stats} />;
    case 'TabbedContentComponent':
      return <TabbedContent {...row.tabbedContent} key={JSON.stringify(row)} />;
    case 'TestimonialsQuoteComponent':
      return <TestimonialsQuote {...row.testimonialsQuote} />;
    case 'TestimonialsTwoColumnComponent':
      return <TestimonialsTwoColumn {...row.testimonialsTwoColumn.testimonialsQuote} {...row.testimonialsTwoColumn} />;
    case 'TextTaglineComponent':
      return <TextTagline {...row.textTagline} />;
    case 'TileListComponent':
      return <TileList {...row.tileList} />;
    case 'TwoColumnComponent':
      return <TwoColumn {...row.twoColumn} />;
    case 'TwoColumnSliderComponent':
      return <TwoColumnSlider rows={row.twoColumnSlider} />;
    case 'VideoComponent':
      return <Video {...row.video} />;
    case 'VideoHeroComponent':
      return <VideoHero {...row.videoHero} key={JSON.stringify(row)} />;
    case 'WhatsNewTimelineComponent':
      return <WhatsNewTimeline {...row.whatsNewTimeline} {...row} key={JSON.stringify(row)} />;
    // ORGANISM_SWITCH_CASE
    default:
      return null;
  }
};

const ComponentLoop = ({
  components = [],
  includeHero = false,
}: {
  components: IComponentLayout[];
  includeHero?: boolean;
}) => {
  const bfcmHeroCarousels = useBfcmCarouselsQuery();
  const { allWpBfcmCalendar:{ nodes: bfcmDates } } = useBfcmCalendarQuery();
  const blogCategories = useBlogCategoryQuery();
  const { glossaryPages, sortedGlossaryPages } = useGlossaryPagesQuery();
  const staticBadges = useG2BadgesLogosQuery();
  const latestBlogPosts = useLatestBlogPostsQuery();
  const newsfeed = useNewsfeedQuery();
  const resources = useResourcesQuery();
  const taxonomies = useTaxonomyQuery();
  const whatsNewUpdates = useWhatsNewUpdateQuery();
  
  return components.map((row, index) => {
    let component = getComponent(row);
    
    if (row?.componentType === 'BfcmCalendarComponent') {
      const bfcmCalendar = {
        ...row.bfcmCalendar,
        bfcmDates,
      };
      component = getComponent({
        ...row,
        bfcmCalendar,
      });
    } else if (row?.componentType === 'BfcmHeroComponent') {
      component = (
        <BfcmHero
          { ...row.bfcmHero }
          key={JSON.stringify(row)}
          customerComponents={bfcmHeroCarousels?.customer?.map((bfcmComponent: IComponentLayout) => getComponent(bfcmComponent)) || []}
          partnerComponents={bfcmHeroCarousels?.partner?.map((bfcmComponent: IComponentLayout) => getComponent(bfcmComponent)) || []}
          prospectComponents={bfcmHeroCarousels?.prospect?.map((bfcmComponent: IComponentLayout) => getComponent(bfcmComponent)) || []}
        />
      );
    } else if (row?.componentType === 'G2BadgesComponent') {
      component = getComponent({
        ...row,
        logosOrganism: staticBadges.logosOrganism,
      });
      
    } else if (row?.componentType === 'BlogCardsComponent') {
      component = getComponent({
        ...row,
        blogCards: {
          ...row.blogCards,
          newsfeed,
          posts: (row.blogCards.postSource === 'latest') ? latestBlogPosts : row.blogCards.posts,
        },
      });
    } else if (row?.componentType === 'GlossaryComponent') {
      component = getComponent({
        ...row,
        terms: glossaryPages || [],
        sortedTerms: sortedGlossaryPages || {},
      });
    } else if (row?.componentType === 'NavigationCategorySelectionComponent') {
      component = getComponent({
        ...row,
        categories: blogCategories || [],
      });
    } else if (row?.componentType === 'PostFilterComponent') {
      /**
       * As more supported post types are added
       * we will need to add more checks here
       **/ 

      // get the taxonomies
      row.postFilter.taxonomies = taxonomies;

      if (row.postFilter.postType === 'resource') {
        row.postFilter.posts = resources || [];
      }
      
      component = getComponent(row);
    } else if (row?.componentType === 'TestimonialsTwoColumnComponent') {
      component = getComponent({
        ...row,
        testimonialsTwoColumn: {
          ...row.testimonialsTwoColumn,
          posts: newsfeed.posts,
        },
      });
    } else if (row?.componentType === 'WhatsNewTimelineComponent') {
      component = getComponent({
        ...row,
        whatsNewTimeline: {
          ...row.whatsNewTimeline,
          whatsNewUpdates: whatsNewUpdates?.allWpWhatsNewUpdate?.nodes,
        },
      });
    }

    // if we have a hero, only eager load the first 2 components otherwise eager load on the first 3
    const threshold = includeHero ? 2 : 3;
    // this will let us know wh
    return <EagerLoadingContextProvider key={JSON.stringify(row)} eagerLoad={index < threshold}>{component}</EagerLoadingContextProvider>;
  });
};

export default ComponentLoop;
